import { type ReactNode } from 'react'
import { BrandPanel } from './BrandPanel.js'

/**
 * Props are unchanged from the pre-M8 layout on purpose: all five auth screens keep working
 * without edits, and ResetPassword/AcceptInvite inherit the new shell for free.
 */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string
  subtitle?: string
  children: ReactNode
  footer?: ReactNode
}) {
  return (
    <div data-auth-root className="auth-shell">
      <style>{`
        .auth-shell {
          min-height: 100vh;
          display: flex;
          background: var(--bg);
        }

        .auth-brand-panel {
          flex: 0 0 44%;
          position: relative;
          overflow: hidden;
          background: var(--mauve-950);
          color: var(--fg-on-mauve);
          padding: 44px 48px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .auth-brand-glow {
          position: absolute;
          inset: 0;
          background: radial-gradient(80% 60% at 20% 100%, rgba(133,79,108,0.35), transparent 70%);
          pointer-events: none;
        }

        .auth-wordmark {
          position: relative;
          font-family: var(--font-display);
          font-weight: 700;
          font-size: 15px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          display: inline-flex;
          align-items: center;
        }

        .auth-wordmark-dot {
          width: 0.32em;
          height: 0.32em;
          background: var(--mauve-400);
          margin-left: 0.32em;
        }

        .auth-metric-card {
          background: rgba(253,250,251,0.06);
          border: 1px solid rgba(253,250,251,0.12);
          border-radius: 12px;
          padding: 18px 20px;
          backdrop-filter: blur(4px);
          animation: auth-bob 7s ease-in-out infinite;
        }

        @keyframes auth-bob {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-8px); }
        }

        @media (prefers-reduced-motion: reduce) {
          .auth-metric-card { animation: none; }
        }

        .auth-metric-label {
          font-family: var(--font-mono);
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: rgba(253,250,251,0.55);
        }

        .auth-metric-stat { font-family: var(--font-display); font-weight: 600; font-size: 34px; }

        .auth-metric-chip {
          background: rgba(107,142,123,0.18);
          color: #a8c7b5;
          border-radius: 999px;
          padding: 2px 8px;
          font-size: 11.5px;
        }

        .auth-metric-caption { font-size: 12px; color: rgba(253,250,251,0.5); margin-top: 2px; }

        .auth-tagline {
          font-family: 'Instrument Serif', Georgia, serif;
          font-style: italic;
          font-size: 30px;
          line-height: 1.25;
          margin: 0;
        }

        .auth-subcopy {
          font-size: 14px;
          line-height: 1.6;
          color: rgba(253,250,251,0.65);
          margin: 12px 0 0;
        }

        .auth-trust { position: relative; font-size: 12px; color: rgba(253,250,251,0.4); }

        .auth-form-panel {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px;
          overflow-y: auto;
        }

        .auth-form-column { width: 100%; max-width: 380px; }

        /* Removed, not stacked: a stacked brand panel pushes the email field below the fold. */
        @media (max-width: 900px) {
          .auth-brand-panel { display: none; }
          .auth-form-panel { padding: 32px 20px; align-items: flex-start; }
        }
      `}</style>

      <BrandPanel />

      <div className="auth-form-panel">
        <div className="auth-form-column">
          <h1 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 600, letterSpacing: '-0.01em' }}>
            {title}
          </h1>
          {subtitle !== undefined ? (
            <p style={{ margin: '6px 0 24px', color: 'var(--fg-muted)', fontSize: 13 }}>{subtitle}</p>
          ) : (
            <div style={{ height: 24 }} />
          )}
          {children}
          {footer !== undefined ? (
            <div style={{ marginTop: 24, fontSize: 13, color: 'var(--fg-muted)' }}>{footer}</div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
